import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Users, Check, Loader2 } from 'lucide-react';
import { Avatar } from '@clareefai/ui';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';

interface AssignSalespersonModalProps {
  currentSalespersonId?: string;
  onAssign: (salespersonId: string, salespersonName: string) => void;
  onClose: () => void;
}

const AssignSalespersonModal: React.FC<AssignSalespersonModalProps> = ({
  currentSalespersonId,
  onAssign,
  onClose
}) => {
  const { t } = useTranslation();
  const [selectedSalespersonId, setSelectedSalespersonId] = useState(currentSalespersonId || '');
  const { data: teamMembers = [], isLoading } = useGetTeamMembersQuery();

  const handleAssign = () => {
    const selectedMember = teamMembers.find((m) => m.id === selectedSalespersonId);
    if (selectedMember) {
      onAssign(selectedMember.id, selectedMember.name);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#4339A0] rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {currentSalespersonId ? t('prospects.assign.changeTitle') : t('prospects.assign.title')}
              </h2>
              <p className="text-sm text-gray-600">{t('prospects.assign.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-10 h-10 animate-spin text-[#4339A0]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teamMembers.map((member) => {
                const isSelected = selectedSalespersonId === member.id;
                const isCurrent = currentSalespersonId === member.id;
                const activeProspects = member.stats?.activeProspects ?? 0;
                const convertedProspects = member.stats?.convertedProspects ?? 0;

                return (
                  <div
                    key={member.id}
                    onClick={() => setSelectedSalespersonId(member.id)}
                    className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[#4339A0] bg-[#f5f3ff] shadow-md'
                        : 'border-gray-200 hover:border-[#4339A0] hover:bg-gray-50'
                    }`}
                  >
                    {isCurrent && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 bg-[#4339A0] text-white text-xs font-medium rounded-full">
                          {t('prospects.assign.current')}
                        </span>
                      </div>
                    )}

                    <div className="flex items-start space-x-3">
                      <Avatar
                        src={typeof member.avatar === 'string' && member.avatar.length > 2 ? member.avatar : undefined}
                        name={member.name}
                        size="md"
                        className="flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{member.name}</h3>
                        <p className="text-sm text-gray-600">{member.role}</p>
                        <p className="text-xs text-gray-500 mt-1">{member.email}</p>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('prospects.assign.activeProspects')}</div>
                          <div className="text-lg font-semibold text-[#4339A0]">
                            {activeProspects}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('prospects.assign.converted')}</div>
                          <div className="text-lg font-semibold text-green-600">
                            {convertedProspects}
                          </div>
                        </div>
                      </div>
                    </div>

                    {isSelected && (
                      <div className="absolute top-1/2 right-4 -translate-y-1/2">
                        <div className="w-8 h-8 bg-[#4339A0] rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {!isLoading && teamMembers.length === 0 && (
            <div className="text-center py-8 text-gray-500">{t('prospects.assign.noTeamMembers')}</div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedSalespersonId}
            className="px-6 py-2.5 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentSalespersonId ? t('prospects.assign.changeButton') : t('prospects.assign.assignButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignSalespersonModal;
